import { injectable } from 'inversify';
import type { Shape } from 'bpmn-js/lib/model/Types';
import { FLOW_LABEL_INDENT } from 'bpmn-js/lib/util/LabelUtil';
import type { Point, RectTRBL as Rect } from 'diagram-js/lib/util/Types';

import {
  HALF,
  FLOW_LABEL_TSPAN_PADDING,
  RETURN_ROUTE_GAP,
  RETURN_ROUTE_LABEL_HEIGHT,
} from '../../constants/eventTypes';
import type {
  AnchorCandidate,
  FlowNode,
  FlowSchema,
  GridNode,
  GridRouteContext,
  OrthogonalDirection,
  PriorityQueueEntry,
  ReturnRouteContext,
  RouteTerminal,
  Segment,
} from '../../types/event-types';

const CONNECTION_BORDER_WIDTH = 2;
const LABEL_OVERHEAD_X = FLOW_LABEL_INDENT + FLOW_LABEL_TSPAN_PADDING;
const RETURN_ROUTE_STUB_OFFSET = 16;
const RETURN_ROUTE_TOP_ANCHOR_OFFSET = 35;
const RETURN_ROUTE_TURN_PENALTY = 320;
const RETURN_ROUTE_SEARCH_MARGIN = 106;
const RETURN_ROUTE_GRAPH_OFFSET = 1;
const MAIN_BRANCH_LABEL_WIDTH = 136;
const SECONDARY_BRANCH_LABEL_WIDTH = 119;
const BRANCH_LABEL_X_OFFSET = 7;

@injectable()
export class EventTypesReturnConnectionService {
  getDraggingReturnSourcePoint(source: Shape, cursorPoint: Point): Point {
    const sourceCenter = this.getNodeCenter(source);

    return cursorPoint.x >= sourceCenter.x
      ? {
          x: source.x + source.width,
          y: sourceCenter.y,
        }
      : { x: source.x, y: sourceCenter.y };
  }

  getDraggingReturnWaypoints(source: Shape, cursorPoint: Point): Point[] {
    return [
      this.getDraggingReturnSourcePoint(source, cursorPoint),
      cursorPoint,
    ];
  }

  getReturnTargetPreviewPointByRect(source: Shape, targetRect: Rect): Point {
    const sourceCenter = this.getNodeCenter(source);
    const targetCenterX = (targetRect.left + targetRect.right) / HALF;

    return this.getPointByRect(
      targetRect,
      targetCenterX >= sourceCenter.x ? 'left' : 'right',
    );
  }

  isReturnNode(node: FlowNode | null | undefined): boolean {
    return Boolean(node && node.type !== 'SystemTask' && node.allowReturnTo);
  }

  buildReturnRoute(
    source: Shape,
    target: Shape,
    context: ReturnRouteContext,
  ): Point[] {
    const {
      nodeObstacles,
      groupObstacles,
      groupObstacleBySchemaId,
      labelObstacleRects,
      connectionObstacles,
      sourceParallelGroupId,
      targetParallelGroupId,
      preferTopCenterSource,
      preferTopCenterTarget,
      gap,
    } = context;
    const sourceRect = this.getShapeRect(source);
    const targetRect = this.getShapeRect(target);
    const sourceGroupRect = sourceParallelGroupId
      ? (groupObstacleBySchemaId.get(sourceParallelGroupId) ?? null)
      : null;
    const targetGroupRect = targetParallelGroupId
      ? (groupObstacleBySchemaId.get(targetParallelGroupId) ?? null)
      : null;
    const obstacleRects = [
      ...nodeObstacles
        .filter(
          (nodeObstacle) =>
            nodeObstacle.shape.id !== source.id &&
            nodeObstacle.shape.id !== target.id,
        )
        .map((nodeObstacle) =>
          this.inflateRect(this.getShapeRect(nodeObstacle.shape), gap),
        ),
      ...groupObstacles
        .filter(
          (groupObstacle) =>
            groupObstacle.schemaId !== sourceParallelGroupId &&
            groupObstacle.schemaId !== targetParallelGroupId,
        )
        .map((groupObstacle) =>
          this.inflateRect(this.getShapeRect(groupObstacle.shape), gap),
        ),
      ...labelObstacleRects.map((rect) => this.inflateRect(rect, gap)),
    ];
    const filteredObstacleSegments = connectionObstacles.filter((segment) => {
      const touchesSource =
        this.isPointInsideRect(segment.start, sourceRect) ||
        this.isPointInsideRect(segment.end, sourceRect);
      const touchesTarget =
        this.isPointInsideRect(segment.start, targetRect) ||
        this.isPointInsideRect(segment.end, targetRect);

      return !touchesSource && !touchesTarget;
    });
    const sourceCandidates = this.getReturnAnchorCandidates(
      source,
      preferTopCenterSource,
    );
    const targetCandidates = this.getReturnAnchorCandidates(
      target,
      preferTopCenterTarget,
    );
    const candidates: Point[][] = [];

    sourceCandidates.forEach((sourceCandidate) => {
      const sourceTerminal = this.createRouteTerminal(
        sourceCandidate,
        RETURN_ROUTE_STUB_OFFSET,
      );

      targetCandidates.forEach((targetCandidate) => {
        const targetTerminal = this.createRouteTerminal(
          targetCandidate,
          RETURN_ROUTE_STUB_OFFSET,
        );
        const gridContext = {
          obstacleRects,
          obstacleSegments: filteredObstacleSegments,
          sourceRect,
          targetRect,
          sourceGroupRect: sourceGroupRect
            ? this.inflateRect(this.getShapeRect(sourceGroupRect), gap)
            : null,
          targetGroupRect: targetGroupRect
            ? this.inflateRect(this.getShapeRect(targetGroupRect), gap)
            : null,
        };
        const route = this.findOrthogonalGridPath(
          sourceTerminal,
          targetTerminal,
          gridContext,
        );

        if (route) {
          candidates.push(this.simplifyWaypoints(route));
          return;
        }

        const routeWithoutConnectionObstacles = this.findOrthogonalGridPath(
          sourceTerminal,
          targetTerminal,
          {
            ...gridContext,
            obstacleSegments: [],
          },
        );

        if (routeWithoutConnectionObstacles) {
          candidates.push(
            this.simplifyWaypoints(routeWithoutConnectionObstacles),
          );
        }
      });
    });

    if (candidates.length) {
      return [...candidates].sort((leftRoute, rightRoute) => {
        const costDifference =
          this.calculatePathCost(leftRoute) -
          this.calculatePathCost(rightRoute);

        if (costDifference !== 0) {
          return costDifference;
        }

        return rightRoute[0].x - leftRoute[0].x;
      })[0];
    }

    if (gap > 0) {
      return this.buildReturnRoute(source, target, {
        ...context,
        gap: 0,
      });
    }

    return this.simplifyWaypoints([
      this.getNodeCenter(source),
      this.getNodeCenter(target),
    ]);
  }

  toSegments(points: Point[]): Segment[] {
    return points.slice(1).map((point, index) => ({
      start: points[index],
      end: point,
    }));
  }

  getBranchLabelObstacleRect(
    edge: FlowSchema['edges'][number],
    source: Shape,
  ): Rect | null {
    const labelBounds = this.getBranchLabelBounds(edge, source);

    if (!labelBounds) {
      return null;
    }

    return this.createRectByBounds({
      ...labelBounds,
      y: source.y - RETURN_ROUTE_LABEL_HEIGHT,
    });
  }

  private getNodeCenter(shape: Shape): Point {
    return {
      x: shape.x + shape.width / HALF,
      y: shape.y + shape.height / HALF,
    };
  }

  private getPointByRect(rect: Rect, side: OrthogonalDirection): Point {
    if (side === 'left') {
      return {
        x: rect.left,
        y: (rect.top + rect.bottom) / HALF,
      };
    }

    if (side === 'right') {
      return {
        x: rect.right,
        y: (rect.top + rect.bottom) / HALF,
      };
    }

    if (side === 'up') {
      return {
        x: (rect.left + rect.right) / HALF,
        y: rect.top,
      };
    }

    return {
      x: (rect.left + rect.right) / HALF,
      y: rect.bottom,
    };
  }

  private getShapeRect(shape: Shape): Rect {
    return {
      left: shape.x,
      right: shape.x + shape.width,
      top: shape.y,
      bottom: shape.y + shape.height,
    };
  }

  private inflateRect(rect: Rect, gap: number): Rect {
    return {
      left: rect.left - gap,
      right: rect.right + gap,
      top: rect.top - gap,
      bottom: rect.bottom + gap,
    };
  }

  private normalizeSegment(segment: Segment): Segment {
    if (
      segment.start.x < segment.end.x ||
      segment.start.y < segment.end.y ||
      (segment.start.x === segment.end.x && segment.start.y === segment.end.y)
    ) {
      return segment;
    }

    return {
      start: segment.end,
      end: segment.start,
    };
  }

  private isPointInsideRect(point: Point, rect: Rect): boolean {
    return (
      point.x >= rect.left &&
      point.x <= rect.right &&
      point.y >= rect.top &&
      point.y <= rect.bottom
    );
  }

  private isPointInsideRectInterior(point: Point, rect: Rect): boolean {
    return (
      point.x > rect.left &&
      point.x < rect.right &&
      point.y > rect.top &&
      point.y < rect.bottom
    );
  }

  private rangesOverlapStrict(
    firstStart: number,
    firstEnd: number,
    secondStart: number,
    secondEnd: number,
  ): boolean {
    const left = Math.max(
      Math.min(firstStart, firstEnd),
      Math.min(secondStart, secondEnd),
    );
    const right = Math.min(
      Math.max(firstStart, firstEnd),
      Math.max(secondStart, secondEnd),
    );

    return right > left;
  }

  private orthSegmentIntersectsRectInterior(
    segment: Segment,
    rect: Rect,
  ): boolean {
    if (segment.start.x === segment.end.x) {
      return (
        segment.start.x > rect.left &&
        segment.start.x < rect.right &&
        this.rangesOverlapStrict(
          segment.start.y,
          segment.end.y,
          rect.top,
          rect.bottom,
        )
      );
    }

    return (
      segment.start.y > rect.top &&
      segment.start.y < rect.bottom &&
      this.rangesOverlapStrict(
        segment.start.x,
        segment.end.x,
        rect.left,
        rect.right,
      )
    );
  }

  private segmentIntersectsRect(segment: Segment, rect: Rect): boolean {
    return (
      this.isPointInsideRectInterior(segment.start, rect) ||
      this.isPointInsideRectInterior(segment.end, rect) ||
      this.orthSegmentIntersectsRectInterior(segment, rect)
    );
  }

  private getDirection(from: Point, to: Point): OrthogonalDirection | null {
    if (from.x === to.x) {
      if (to.y > from.y) {
        return 'down';
      }

      if (to.y < from.y) {
        return 'up';
      }

      return null;
    }

    if (from.y === to.y) {
      return to.x > from.x ? 'right' : 'left';
    }

    return null;
  }

  private movePoint(
    point: Point,
    direction: OrthogonalDirection,
    distance: number,
  ): Point {
    if (direction === 'left') {
      return { x: point.x - distance, y: point.y };
    }

    if (direction === 'right') {
      return { x: point.x + distance, y: point.y };
    }

    if (direction === 'up') {
      return { x: point.x, y: point.y - distance };
    }

    return { x: point.x, y: point.y + distance };
  }

  private getReturnAnchorCandidates(
    shape: Shape,
    preferTopCenter = false,
  ): AnchorCandidate[] {
    const rect = this.getShapeRect(shape);

    if (preferTopCenter) {
      const centerPoint = this.getPointByRect(rect, 'up');
      const halfWidth = (rect.right - rect.left) / HALF;
      const topAnchorOffset = Math.min(
        RETURN_ROUTE_TOP_ANCHOR_OFFSET,
        Math.max(0, halfWidth - 1),
      );

      if (topAnchorOffset === 0) {
        return [
          {
            point: centerPoint,
            direction: 'up',
          },
        ];
      }

      return [
        {
          point: { x: centerPoint.x - topAnchorOffset, y: rect.top },
          direction: 'up',
        },
        {
          point: { x: centerPoint.x + topAnchorOffset, y: rect.top },
          direction: 'up',
        },
      ];
    }

    return [
      {
        point: this.getPointByRect(rect, 'left'),
        direction: 'left',
      },
      {
        point: this.getPointByRect(rect, 'right'),
        direction: 'right',
      },
    ];
  }

  private createRouteTerminal(
    candidate: AnchorCandidate,
    stubOffset: number,
  ): RouteTerminal {
    return {
      anchor: candidate.point,
      stub: this.movePoint(candidate.point, candidate.direction, stubOffset),
      direction: candidate.direction,
    };
  }

  private getSearchBounds(
    obstacleRects: Rect[],
    obstacleSegments: Segment[],
    terminals: [RouteTerminal, RouteTerminal],
    terminalRects: [Rect, Rect],
  ): Rect {
    const [sourceTerminal, targetTerminal] = terminals;
    const [sourceRect, targetRect] = terminalRects;
    const pointRects = [
      sourceRect,
      targetRect,
      ...obstacleRects,
      {
        left: sourceTerminal.stub.x,
        right: sourceTerminal.stub.x,
        top: sourceTerminal.stub.y,
        bottom: sourceTerminal.stub.y,
      },
      {
        left: targetTerminal.stub.x,
        right: targetTerminal.stub.x,
        top: targetTerminal.stub.y,
        bottom: targetTerminal.stub.y,
      },
    ];
    const bounds = pointRects.reduce(
      (acc, rect) => ({
        left: Math.min(acc.left, rect.left),
        right: Math.max(acc.right, rect.right),
        top: Math.min(acc.top, rect.top),
        bottom: Math.max(acc.bottom, rect.bottom),
      }),
      sourceRect,
    );

    const segmentBounds = obstacleSegments.reduce(
      (acc, segment) => ({
        left: Math.min(acc.left, segment.start.x, segment.end.x),
        right: Math.max(acc.right, segment.start.x, segment.end.x),
        top: Math.min(acc.top, segment.start.y, segment.end.y),
        bottom: Math.max(acc.bottom, segment.start.y, segment.end.y),
      }),
      bounds,
    );

    return {
      left: segmentBounds.left - RETURN_ROUTE_SEARCH_MARGIN,
      right: segmentBounds.right + RETURN_ROUTE_SEARCH_MARGIN,
      top: segmentBounds.top - RETURN_ROUTE_SEARCH_MARGIN,
      bottom: segmentBounds.bottom + RETURN_ROUTE_SEARCH_MARGIN,
    };
  }

  private addRectCorridorCoordinates(
    values: Set<number>,
    start: number,
    end: number,
  ): void {
    values.add(start);
    values.add(end);
    values.add(start - RETURN_ROUTE_GRAPH_OFFSET);
    values.add(end + RETURN_ROUTE_GRAPH_OFFSET);
  }

  private rectsIntersect(first: Rect, second: Rect): boolean {
    return !(
      first.right < second.left ||
      first.left > second.right ||
      first.bottom < second.top ||
      first.top > second.bottom
    );
  }

  private segmentBounds(segment: Segment): Rect {
    return {
      left: Math.min(segment.start.x, segment.end.x),
      right: Math.max(segment.start.x, segment.end.x),
      top: Math.min(segment.start.y, segment.end.y),
      bottom: Math.max(segment.start.y, segment.end.y),
    };
  }

  private rectIntersectsSegment(rect: Rect, segment: Segment): boolean {
    return this.rectsIntersect(rect, this.segmentBounds(segment));
  }

  private collectGridCoordinates(
    sourceTerminal: RouteTerminal,
    targetTerminal: RouteTerminal,
    obstacleRects: Rect[],
    obstacleSegments: Segment[],
    bounds: Rect,
  ): { x: number[]; y: number[] } {
    const xValues = new Set<number>([
      bounds.left,
      bounds.right,
      sourceTerminal.anchor.x,
      sourceTerminal.stub.x,
      targetTerminal.anchor.x,
      targetTerminal.stub.x,
    ]);
    const yValues = new Set<number>([
      bounds.top,
      bounds.bottom,
      sourceTerminal.anchor.y,
      sourceTerminal.stub.y,
      targetTerminal.anchor.y,
      targetTerminal.stub.y,
    ]);

    obstacleRects
      .filter((rect) => this.rectsIntersect(rect, bounds))
      .forEach((rect) => {
        this.addRectCorridorCoordinates(xValues, rect.left, rect.right);
        this.addRectCorridorCoordinates(yValues, rect.top, rect.bottom);
      });

    obstacleSegments
      .filter((segment) => this.rectIntersectsSegment(bounds, segment))
      .forEach((segment) => {
        xValues.add(segment.start.x);
        yValues.add(segment.start.y);

        if (segment.start.x === segment.end.x) {
          xValues.add(segment.start.x - RETURN_ROUTE_GRAPH_OFFSET);
          xValues.add(segment.start.x + RETURN_ROUTE_GRAPH_OFFSET);
          yValues.add(segment.end.y);
        }

        if (segment.start.y === segment.end.y) {
          yValues.add(segment.start.y - RETURN_ROUTE_GRAPH_OFFSET);
          yValues.add(segment.start.y + RETURN_ROUTE_GRAPH_OFFSET);
          xValues.add(segment.end.x);
        }
      });

    return {
      x: [...xValues].sort((left, right) => left - right),
      y: [...yValues].sort((left, right) => left - right),
    };
  }

  private createNodeKey(node: GridNode): string {
    return `${node.x}:${node.y}:${node.direction ?? 'none'}`;
  }

  private getPointKey(point: Point): string {
    return `${point.x}:${point.y}`;
  }

  private isPointBlockedByRectangles(
    point: Point,
    obstacleRects: Rect[],
  ): boolean {
    return obstacleRects.some((rect) => this.isPointInsideRect(point, rect));
  }

  private isAllowedTerminalSegment(
    segment: Segment,
    sourceTerminal: RouteTerminal,
    targetTerminal: RouteTerminal,
  ): boolean {
    const normalized = this.normalizeSegment(segment);
    const allowedSegments = [
      this.normalizeSegment({
        start: sourceTerminal.anchor,
        end: sourceTerminal.stub,
      }),
      this.normalizeSegment({
        start: targetTerminal.anchor,
        end: targetTerminal.stub,
      }),
    ];

    return allowedSegments.some(
      (allowedSegment) =>
        allowedSegment.start.x === normalized.start.x &&
        allowedSegment.start.y === normalized.start.y &&
        allowedSegment.end.x === normalized.end.x &&
        allowedSegment.end.y === normalized.end.y,
    );
  }

  private createSegmentCorridorRect(segment: Segment, gap: number): Rect {
    const normalized = this.normalizeSegment(segment);

    if (normalized.start.x === normalized.end.x) {
      return {
        left: normalized.start.x - gap,
        right: normalized.start.x + gap,
        top: Math.min(normalized.start.y, normalized.end.y),
        bottom: Math.max(normalized.start.y, normalized.end.y),
      };
    }

    return {
      left: Math.min(normalized.start.x, normalized.end.x),
      right: Math.max(normalized.start.x, normalized.end.x),
      top: normalized.start.y - gap,
      bottom: normalized.start.y + gap,
    };
  }

  private segmentHitsObstacleSegment(
    segment: Segment,
    obstacleSegments: Segment[],
    gap: number,
  ): boolean {
    const normalized = this.normalizeSegment(segment);

    return obstacleSegments.some((obstacleSegment) =>
      this.segmentIntersectsRect(
        normalized,
        this.createSegmentCorridorRect(obstacleSegment, gap),
      ),
    );
  }

  private isInsideTerminalCorridor(
    segment: Segment,
    rect: Rect,
    terminal: RouteTerminal,
  ): boolean {
    const normalized = this.normalizeSegment(segment);

    if (terminal.direction === 'up') {
      return (
        normalized.start.x === normalized.end.x &&
        normalized.start.x === terminal.anchor.x &&
        normalized.start.y <= terminal.anchor.y &&
        normalized.end.y >= rect.top &&
        normalized.end.y <= terminal.anchor.y
      );
    }

    return false;
  }

  private segmentHitsTerminalRect(
    segment: Segment,
    rect: Rect,
    sourceTerminal: RouteTerminal,
    targetTerminal: RouteTerminal,
  ): boolean {
    const normalized = this.normalizeSegment(segment);

    if (
      this.isAllowedTerminalSegment(
        normalized,
        sourceTerminal,
        targetTerminal,
      ) ||
      this.isInsideTerminalCorridor(normalized, rect, sourceTerminal) ||
      this.isInsideTerminalCorridor(normalized, rect, targetTerminal)
    ) {
      return false;
    }

    return this.segmentIntersectsRect(normalized, rect);
  }

  private isSegmentPassable(
    segment: Segment,
    context: GridRouteContext,
    sourceTerminal: RouteTerminal,
    targetTerminal: RouteTerminal,
  ): boolean {
    const normalized = this.normalizeSegment(segment);

    if (
      normalized.start.x === normalized.end.x &&
      normalized.start.y === normalized.end.y
    ) {
      return false;
    }

    if (
      context.obstacleRects.some((rect) =>
        this.segmentIntersectsRect(normalized, rect),
      )
    ) {
      return false;
    }

    if (
      this.segmentHitsObstacleSegment(
        normalized,
        context.obstacleSegments,
        RETURN_ROUTE_GAP,
      )
    ) {
      return false;
    }

    if (
      this.segmentHitsTerminalRect(
        normalized,
        context.sourceRect,
        sourceTerminal,
        targetTerminal,
      ) ||
      this.segmentHitsTerminalRect(
        normalized,
        context.targetRect,
        sourceTerminal,
        targetTerminal,
      ) ||
      (context.sourceGroupRect &&
        this.segmentHitsTerminalRect(
          normalized,
          context.sourceGroupRect,
          sourceTerminal,
          targetTerminal,
        )) ||
      (context.targetGroupRect &&
        this.segmentHitsTerminalRect(
          normalized,
          context.targetGroupRect,
          sourceTerminal,
          targetTerminal,
        ))
    ) {
      return false;
    }

    return true;
  }

  private getFreePointNeighbors({
    point,
    grid,
    gridIndexByCoordinate,
    context,
    sourceTerminal,
    targetTerminal,
  }: {
    point: Point;
    grid: { x: number[]; y: number[] };
    gridIndexByCoordinate: {
      x: Map<number, number>;
      y: Map<number, number>;
    };
    context: GridRouteContext;
    sourceTerminal: RouteTerminal;
    targetTerminal: RouteTerminal;
  }): Point[] {
    const xIndex = gridIndexByCoordinate.x.get(point.x) ?? -1;
    const yIndex = gridIndexByCoordinate.y.get(point.y) ?? -1;
    const candidates: Point[] = [];

    if (xIndex > 0) {
      candidates.push({ x: grid.x[xIndex - 1], y: point.y });
    }

    if (xIndex < grid.x.length - 1) {
      candidates.push({ x: grid.x[xIndex + 1], y: point.y });
    }

    if (yIndex > 0) {
      candidates.push({ x: point.x, y: grid.y[yIndex - 1] });
    }

    if (yIndex < grid.y.length - 1) {
      candidates.push({ x: point.x, y: grid.y[yIndex + 1] });
    }

    return candidates.filter((candidate) => {
      if (
        this.isPointBlockedByRectangles(candidate, context.obstacleRects) ||
        this.isPointInsideRectInterior(candidate, context.sourceRect) ||
        this.isPointInsideRectInterior(candidate, context.targetRect)
      ) {
        return false;
      }

      return this.isSegmentPassable(
        {
          start: point,
          end: candidate,
        },
        context,
        sourceTerminal,
        targetTerminal,
      );
    });
  }

  private reconstructGridPath(
    previousByKey: Map<string, GridNode>,
    endNode: GridNode,
  ): Point[] {
    const points: Point[] = [];
    let current: GridNode | undefined = endNode;

    while (current) {
      points.push({ x: current.x, y: current.y });
      current = previousByKey.get(this.createNodeKey(current));
    }

    return points.reverse();
  }

  private simplifyWaypoints(points: Point[]): Point[] {
    return points.reduce<Point[]>((acc, point) => {
      if (!acc.length) {
        return [point];
      }

      const last = acc[acc.length - 1];

      if (last.x === point.x && last.y === point.y) {
        return acc;
      }

      if (acc.length < HALF) {
        return [...acc, point];
      }

      const beforeLast = acc[acc.length - HALF];
      const sameVertical = beforeLast.x === last.x && last.x === point.x;
      const sameHorizontal = beforeLast.y === last.y && last.y === point.y;

      if (sameVertical || sameHorizontal) {
        return [...acc.slice(0, -1), point];
      }

      return [...acc, point];
    }, []);
  }

  private calculatePathCost(points: Point[]): number {
    return points.slice(1).reduce((total, point, index) => {
      const previous = points[index];
      const segmentLength =
        Math.abs(point.x - previous.x) + Math.abs(point.y - previous.y);
      const nextTotal = total + segmentLength;

      if (index === 0) {
        return nextTotal;
      }

      const beforePrevious = points[index - 1];
      const previousDirection = this.getDirection(beforePrevious, previous);
      const nextDirection = this.getDirection(previous, point);

      return previousDirection &&
        nextDirection &&
        previousDirection !== nextDirection
        ? nextTotal + RETURN_ROUTE_TURN_PENALTY
        : nextTotal;
    }, 0);
  }

  private pushPriorityQueue(
    heap: PriorityQueueEntry[],
    entry: PriorityQueueEntry,
  ): void {
    heap.push(entry);
    let index = heap.length - 1;

    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / HALF);

      if (heap[parentIndex].priority <= heap[index].priority) {
        break;
      }

      [heap[parentIndex], heap[index]] = [heap[index], heap[parentIndex]];
      index = parentIndex;
    }
  }

  private popPriorityQueue(
    heap: PriorityQueueEntry[],
  ): PriorityQueueEntry | null {
    if (!heap.length) {
      return null;
    }

    const top = heap[0];
    const last = heap.pop();

    if (!heap.length || !last) {
      return top;
    }

    heap[0] = last;
    let index = 0;

    while (true) {
      const leftIndex = index * HALF + 1;
      const rightIndex = leftIndex + 1;
      let smallestIndex = index;

      if (
        leftIndex < heap.length &&
        heap[leftIndex].priority < heap[smallestIndex].priority
      ) {
        smallestIndex = leftIndex;
      }

      if (
        rightIndex < heap.length &&
        heap[rightIndex].priority < heap[smallestIndex].priority
      ) {
        smallestIndex = rightIndex;
      }

      if (smallestIndex === index) {
        break;
      }

      [heap[index], heap[smallestIndex]] = [heap[smallestIndex], heap[index]];
      index = smallestIndex;
    }

    return top;
  }

  private createHeuristic(point: Point, target: Point): number {
    return Math.abs(target.x - point.x) + Math.abs(target.y - point.y);
  }

  private findOrthogonalGridPath(
    sourceTerminal: RouteTerminal,
    targetTerminal: RouteTerminal,
    context: GridRouteContext,
  ): Point[] | null {
    const bounds = this.getSearchBounds(
      context.obstacleRects,
      context.obstacleSegments,
      [sourceTerminal, targetTerminal],
      [context.sourceRect, context.targetRect],
    );
    const grid = this.collectGridCoordinates(
      sourceTerminal,
      targetTerminal,
      context.obstacleRects,
      context.obstacleSegments,
      bounds,
    );
    const gridIndexByCoordinate = {
      x: new Map(grid.x.map((coordinate, index) => [coordinate, index])),
      y: new Map(grid.y.map((coordinate, index) => [coordinate, index])),
    };
    const targetPointKey = this.getPointKey(targetTerminal.stub);
    const startNode: GridNode = {
      x: sourceTerminal.stub.x,
      y: sourceTerminal.stub.y,
      direction: sourceTerminal.direction,
    };
    const openNodes: PriorityQueueEntry[] = [];
    const startKey = this.createNodeKey(startNode);
    const costByKey = new Map<string, number>([[startKey, 0]]);
    const previousByKey = new Map<string, GridNode>();

    this.pushPriorityQueue(openNodes, {
      node: startNode,
      priority: this.createHeuristic(sourceTerminal.stub, targetTerminal.stub),
    });

    while (openNodes.length) {
      const currentEntry = this.popPriorityQueue(openNodes);

      if (!currentEntry) {
        break;
      }

      const current = currentEntry.node;
      const currentKey = this.createNodeKey(current);
      const currentCost = costByKey.get(currentKey);

      if (currentCost === undefined) {
        continue;
      }

      const expectedPriority =
        currentCost + this.createHeuristic(current, targetTerminal.stub);

      if (currentEntry.priority > expectedPriority) {
        continue;
      }

      if (this.getPointKey(current) === targetPointKey) {
        return [
          sourceTerminal.anchor,
          ...this.reconstructGridPath(previousByKey, current),
          targetTerminal.anchor,
        ];
      }

      const neighbors = this.getFreePointNeighbors({
        point: current,
        grid,
        gridIndexByCoordinate,
        context,
        sourceTerminal,
        targetTerminal,
      });

      neighbors.forEach((neighborPoint) => {
        const direction = this.getDirection(current, neighborPoint);

        if (!direction) {
          return;
        }

        const turnPenalty =
          current.direction && current.direction !== direction
            ? RETURN_ROUTE_TURN_PENALTY
            : 0;
        const distance =
          Math.abs(neighborPoint.x - current.x) +
          Math.abs(neighborPoint.y - current.y);
        const tentativeCost = currentCost + distance + turnPenalty;
        const neighborNode: GridNode = {
          x: neighborPoint.x,
          y: neighborPoint.y,
          direction,
        };
        const neighborKey = this.createNodeKey(neighborNode);

        if (
          tentativeCost >=
          (costByKey.get(neighborKey) ?? Number.POSITIVE_INFINITY)
        ) {
          return;
        }

        previousByKey.set(neighborKey, current);
        costByKey.set(neighborKey, tentativeCost);
        this.pushPriorityQueue(openNodes, {
          node: neighborNode,
          priority:
            tentativeCost +
            this.createHeuristic(neighborPoint, targetTerminal.stub),
        });
      });
    }

    return null;
  }

  private createRectByBounds(bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): Rect {
    return {
      left: bounds.x,
      right: bounds.x + bounds.width,
      top: bounds.y,
      bottom: bounds.y + bounds.height,
    };
  }

  private getBranchLabelBounds(
    edge: FlowSchema['edges'][number],
    source: Shape,
  ): { x: number; y: number; width: number; height: number } | null {
    if (edge.condition !== 'main' && edge.condition !== 'secondary') {
      return null;
    }

    const startX = source.x + source.width / HALF;

    if (edge.condition === 'main') {
      return {
        x:
          startX -
          MAIN_BRANCH_LABEL_WIDTH -
          LABEL_OVERHEAD_X -
          BRANCH_LABEL_X_OFFSET,
        y: 0,
        width: MAIN_BRANCH_LABEL_WIDTH,
        height: RETURN_ROUTE_LABEL_HEIGHT,
      };
    }

    return {
      x:
        startX -
        LABEL_OVERHEAD_X +
        CONNECTION_BORDER_WIDTH +
        BRANCH_LABEL_X_OFFSET,
      y: 0,
      width: SECONDARY_BRANCH_LABEL_WIDTH,
      height: RETURN_ROUTE_LABEL_HEIGHT,
    };
  }
}
